if (!customElements.get('product-add-ons')) {
  customElements.define(
    'product-add-ons',
    class ProductAddOns extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        this.addEventListener('change', this.onSelectionChange);
        this.form?.addEventListener('change', this.onParentQuantityChange);
        this.form?.addEventListener('input', this.onParentQuantityChange);
        window.addEventListener('resize', this.onResize);
        this.updateOptionTransitions();
        this.updateDisplayPrices();
        this.updateSubtotal();
        this.updateAcceleratedCheckoutItems();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onSelectionChange);
        this.form?.removeEventListener('change', this.onParentQuantityChange);
        this.form?.removeEventListener('input', this.onParentQuantityChange);
        window.removeEventListener('resize', this.onResize);
      }

      onSelectionChange = () => {
        this.updateOptionTransitions();
        this.updateSubtotal();
        this.updateAcceleratedCheckoutItems();
      };

      onResize = () => {
        this.updateOptionTransitions();
      };

      onParentQuantityChange = (event) => {
        if (!event.target.matches('input[name="quantity"]')) return;
        this.updateDisplayPrices();
        this.updateSubtotal();
        this.updateAcceleratedCheckoutItems();
      };

      getParentQuantity() {
        const quantity = parseInt(this.form?.querySelector('input[name="quantity"]')?.value || this.dataset.parentQuantity || '1');
        return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
      }

      updateDisplayPrices() {
        const parentQuantity = this.getParentQuantity();
        this.querySelectorAll('[data-add-on-group]').forEach((group) => {
          const multiplier = group.dataset.addOnMatchParentQuantity === 'true' ? parentQuantity : 1;
          group.querySelectorAll('[data-add-on-display-price]').forEach((priceElement) => {
            const unitPrice = parseInt(priceElement.dataset.addOnUnitPrice || '0');
            if (!Number.isNaN(unitPrice)) {
              priceElement.innerHTML = Shopify.formatMoney(unitPrice * multiplier, window.moneyFormat);
            }
          });
        });
      }

      updateOptionTransitions() {
        this.querySelectorAll('.product-add-ons__group--variants .product-add-ons__options--optional').forEach(
          (options) => {
            if (options.scrollHeight > 0) {
              options.style.setProperty('--product-add-ons-options-height', `${options.scrollHeight}px`);
            } else {
              options.style.removeProperty('--product-add-ons-options-height');
            }
          }
        );
      }

      updateSubtotal() {
        const subtotal = [...this.querySelectorAll('[data-add-on-group]')].reduce((total, group) => {
          const toggle = group.querySelector('.product-add-ons__toggle');
          if (toggle && !toggle.checked) return total;

          return [...group.querySelectorAll('.product-add-ons__input:checked[data-add-on-variant-id]')].reduce((sum, selectedVariant) => {
            const price = parseInt(selectedVariant.dataset.addOnPrice || '0');
            const quantity = group.dataset.addOnMatchParentQuantity === 'true'
              ? this.getParentQuantity()
              : parseInt(selectedVariant.dataset.addOnQuantityMin || '1');
            return sum + (Number.isNaN(price) ? 0 : price) * (Number.isNaN(quantity) ? 1 : quantity);
          }, total);
        }, 0);
        const subtotalElement = this.querySelector('[data-add-ons-subtotal]');
        if (subtotalElement) subtotalElement.innerHTML = Shopify.formatMoney(subtotal, window.moneyFormat);
      }

      updateAcceleratedCheckoutItems() {
        const container = this.querySelector('[data-add-on-additional-items]');
        const parentVariantId = this.form?.querySelector('.product-variant-id')?.value;
        if (!container || !parentVariantId) return;

        const selectedAddOns = [...this.querySelectorAll('[data-add-on-group]')]
          .filter((group) => {
            const toggle = group.querySelector('.product-add-ons__toggle');
            return !toggle || toggle.checked;
          })
          .flatMap((group) => [...group.querySelectorAll('.product-add-ons__input:checked[data-add-on-variant-id]')]
            .map((input) => ({ group, input })))
          .filter(({ input }) => input?.dataset.addOnVariantId);

        const fields = document.createDocumentFragment();
        selectedAddOns.forEach(({ group, input }, index) => {
          const itemIndex = index + 1;
          const quantity = group.dataset.addOnMatchParentQuantity === 'true'
            ? this.getParentQuantity()
            : parseInt(input.dataset.addOnQuantityMin || '1');
          const values = {
            id: input.dataset.addOnVariantId,
            quantity: Number.isNaN(quantity) || quantity < 1 ? 1 : quantity,
            parent_id: parentVariantId
          };

          Object.entries(values).forEach(([key, value]) => {
            const field = document.createElement('input');
            field.type = 'hidden';
            field.name = `shopify-additional-items[${itemIndex}][${key}]`;
            field.value = value;
            fields.appendChild(field);
          });
        });

        container.replaceChildren(fields);
      }
    }
  );
}

if (!customElements.get('product-add-on-picker')) {
  customElements.define(
    'product-add-on-picker',
    class ProductAddOnPicker extends HTMLElement {
      connectedCallback() {
        this.normalizeControls();
        this.addEventListener('change', this.onOptionChange);
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.removeEventListener('change', this.onOptionChange);
      }

      onOptionChange = (event) => {
        const input = event.target.closest('.product-add-ons__value-input');
        if (!input) return;

        const optionValueIds = [...this.querySelectorAll('.product-add-ons__value-input:checked')]
          .map(({ dataset }) => dataset.optionValueId)
          .filter(Boolean);

        this.renderSelectedOptions(optionValueIds, input.dataset.optionValueId, input.dataset.productUrl);
      };

      renderSelectedOptions(optionValueIds, focusedOptionValueId, productUrl) {
        this.abortController?.abort();
        const abortController = new AbortController();
        this.abortController = abortController;
        this.setAttribute('aria-busy', 'true');

        const requestUrl = new URL(productUrl || this.dataset.url, window.location.origin);
        requestUrl.searchParams.set('section_id', 'product-add-on-picker');
        requestUrl.searchParams.set('option_values', optionValueIds.join(','));

        fetch(requestUrl.toString(), { signal: abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Unable to load add-on options: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const source = html.querySelector('[data-add-on-picker-content]');
            const destination = this.querySelector('[data-add-on-picker-content]');
            if (!source || !destination) throw new Error('Add-on option response is missing picker content.');

            destination.innerHTML = source.innerHTML;
            this.dataset.url = source.dataset.productUrl || requestUrl.pathname;
            this.normalizeControls();
            this.dispatchVariantChange();
            this.closest('product-add-ons')?.updateOptionTransitions();
            this.closest('product-add-ons')?.updateDisplayPrices();
            this.closest('product-add-ons')?.updateSubtotal();
            this.closest('product-add-ons')?.updateAcceleratedCheckoutItems();
            this.querySelector(`[data-option-value-id="${CSS.escape(focusedOptionValueId)}"]`)?.focus();
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            if (this.abortController === abortController) this.removeAttribute('aria-busy');
          });
      }

      normalizeControls() {
        const pickerId = this.dataset.pickerId;
        this.querySelectorAll('[data-add-on-option-position]').forEach((optionGroup) => {
          const position = optionGroup.dataset.addOnOptionPosition;
          optionGroup.querySelectorAll('.product-add-ons__value-input').forEach((input, index) => {
            const id = `${pickerId}-${position}-${index}`;
            input.id = id;
            input.name = `add_on_option_${pickerId}_${position}`;
            input.closest('label')?.setAttribute('for', id);
          });
        });

        const variantInput = this.querySelector('.product-add-ons__input');
        if (variantInput) variantInput.name = `add_on_group_${pickerId}`;
      }

      dispatchVariantChange() {
        this.dispatchEvent(new CustomEvent('addOnPickerVariantChanged', { bubbles: true }));
      }
    }
  );
}
